/**
 * Scope Editor Primitives
 *
 * Low-level UI primitives for the mapping scope editor.
 * Shared across mapping configuration sections.
 */

import React from 'react';
import AddIcon from '@mui/icons-material/Add';
import {
  Button,
  Paper,
  Stack,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  type ButtonProps,
  type TableRowProps,
} from '@mui/material';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import HelpTooltipTrigger from '../common/HelpTooltipTrigger';
import type { HelpValue } from '../common/helpModel';
import {
  borderRadius,
  getControlBorderColor,
  getFieldBorderColor,
  layoutGap,
} from '../../../theme/theme';
import {
  getWorkflowWorkspacePalette,
  type WorkflowWorkspacePalette,
} from '../workflow/WorkflowWorkspacePrimitives';

export interface ScopeEditorPalette {
  backgroundCard: string;
  backgroundHover: string;
  backgroundSelected: string;
  borderStrong: string;
  borderLight: string;
  textPrimary: string;
  textSecondary: string;
  textTertiary: string;
  statusSuccessText: string;
  statusWarningText: string;
  statusInfoText: string;
}

const getScopeEditorPalette = (
  theme: Theme,
  workspacePalette: WorkflowWorkspacePalette
): ScopeEditorPalette => ({
  backgroundCard: workspacePalette.sectionBackground,
  backgroundHover: alpha(
    theme.palette.primary.main,
    theme.palette.mode === 'light' ? 0.06 : 0.04
  ),
  backgroundSelected: alpha(
    theme.palette.primary.main,
    theme.palette.mode === 'light' ? 0.1 : 0.08
  ),
  borderStrong: alpha(
    theme.palette.divider,
    theme.palette.mode === 'light' ? 0.22 : 0.12
  ),
  borderLight: alpha(
    theme.palette.divider,
    theme.palette.mode === 'light' ? 0.12 : 0.06
  ),
  textPrimary: theme.palette.text.primary,
  textSecondary: alpha(
    theme.palette.text.secondary,
    theme.palette.mode === 'light' ? 0.9 : 0.85
  ),
  textTertiary: alpha(
    theme.palette.text.secondary,
    theme.palette.mode === 'light' ? 0.7 : 0.6
  ),
  statusSuccessText: theme.palette.success.light,
  statusWarningText: theme.palette.warning.light,
  statusInfoText: theme.palette.info.light,
});

export const useScopeEditorPalette = (): ScopeEditorPalette => {
  const theme = useTheme();
  return React.useMemo(
    () => getScopeEditorPalette(theme, getWorkflowWorkspacePalette(theme)),
    [theme]
  );
};

// ============================================================================
// DENSITY CONFIGURATION
// ============================================================================

export const scopeEditorDensity = {
  toolbarMinHeight: 40,
  inputHeight: 28,
  rowHeight: 32,
  denseRowHeight: 28,
  headerInputHeight: 32,
  actionSize: 24,
  helpSize: 20,
  cellFontSize: '0.6875rem',
  headerFontSize: '0.625rem',
  metaFontSize: '0.625rem',
} as const;

// ============================================================================
// STYLE HELPERS
// ============================================================================

type SummaryTone = 'default' | 'warning' | 'success' | 'info';

export const getScopeEditorHeaderCellSx = (
  theme: Theme,
  options?: {
    width?: number | string;
    minWidth?: number | string;
    align?: 'left' | 'center' | 'right';
  }
): SxProps<Theme> => {
  const palette = getScopeEditorPalette(theme, getWorkflowWorkspacePalette(theme));

  return {
    fontWeight: 600,
    fontSize: scopeEditorDensity.headerFontSize,
    textTransform: 'uppercase',
    letterSpacing: '0.025em',
    color: palette.textSecondary,
    backgroundColor: alpha(theme.palette.background.default, 0.5),
    borderBottom: `1px solid ${palette.borderStrong}`,
    whiteSpace: 'nowrap',
    py: theme.spacing(0.5),
    px: layoutGap.micro,
    width: options?.width,
    minWidth: options?.minWidth ?? options?.width,
    textAlign: options?.align ?? 'left',
  };
};

// Rest/hover/focus feedback is identical across the dropdown, select, and
// text field primitives; keeping it in one place prevents the three from
// drifting apart. The focus ring is an inset shadow rather than a border
// change so focusing a field never shifts its layout.
const getScopeEditorFieldStatesSx = (theme: Theme) => {
  const focusRingColor = alpha(
    theme.palette.divider,
    theme.palette.mode === 'light' ? 0.22 : 0.18
  );

  return {
    backgroundColor: alpha(theme.palette.background.default, 0.2),
    transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      backgroundColor: alpha(theme.palette.background.default, 0.32),
    },
    '&.Mui-focused': {
      backgroundColor: alpha(theme.palette.background.default, 0.32),
      boxShadow: `inset 0 0 0 2px ${focusRingColor}`,
    },
  };
};

export const getScopeEditorDropdownSx = (
  theme: Theme,
  inputHeight: number = scopeEditorDensity.inputHeight
): SxProps<Theme> => {
  const fieldBorderColor = getFieldBorderColor(theme);

  return {
    '& .MuiOutlinedInput-root': {
      height: inputHeight,
      fontSize: scopeEditorDensity.cellFontSize,
      borderRadius: `${borderRadius.sm}px`,
      ...getScopeEditorFieldStatesSx(theme),
      '& fieldset': {
        borderColor: fieldBorderColor,
        borderRadius: `${borderRadius.sm}px`,
      },
      '&:hover fieldset': {
        borderColor: fieldBorderColor,
      },
      '&.Mui-focused fieldset': {
        borderColor: fieldBorderColor,
        borderWidth: 1,
      },
    },
    '& .MuiAutocomplete-inputRoot': {
      py: 0,
      pl: '0 !important',
    },
    '& .MuiAutocomplete-input': {
      py: '2px !important',
      px: '14px !important',
    },
    '& .MuiAutocomplete-endAdornment': {
      right: 4,
    },
    '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
      width: 22,
      height: 22,
      p: 0,
    },
  };
};

export const getScopeEditorSelectSx = (
  theme: Theme,
  inputHeight: number = scopeEditorDensity.inputHeight
): SxProps<Theme> => {
  const fieldBorderColor = getFieldBorderColor(theme);

  return {
    fontSize: scopeEditorDensity.cellFontSize,
    height: inputHeight,
    borderRadius: `${borderRadius.sm}px`,
    ...getScopeEditorFieldStatesSx(theme),
    '& .MuiSelect-select': {
      py: 0.125,
      px: theme.spacing(1.75),
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: fieldBorderColor,
      borderRadius: `${borderRadius.sm}px`,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: fieldBorderColor,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: fieldBorderColor,
      borderWidth: 1,
    },
  };
};

export const getScopeEditorTextFieldSx = (
  theme: Theme,
  inputHeight: number = scopeEditorDensity.inputHeight
): SxProps<Theme> => {
  const fieldBorderColor = getFieldBorderColor(theme);

  return {
    '& .MuiOutlinedInput-root': {
      height: inputHeight,
      fontSize: scopeEditorDensity.cellFontSize,
      borderRadius: `${borderRadius.sm}px`,
      ...getScopeEditorFieldStatesSx(theme),
      '& fieldset': {
        borderColor: fieldBorderColor,
        borderRadius: `${borderRadius.sm}px`,
      },
      '&:hover fieldset': {
        borderColor: fieldBorderColor,
      },
      '&.Mui-focused fieldset': {
        borderColor: fieldBorderColor,
        borderWidth: 1,
      },
    },
    '& .MuiOutlinedInput-input': {
      py: 0.125,
      px: theme.spacing(1.75),
    },
  };
};

export const getScopeEditorActionButtonSx = (
  theme: Theme,
  tone: 'default' | 'danger' = 'default'
): SxProps<Theme> => {
  const palette = getScopeEditorPalette(theme, getWorkflowWorkspacePalette(theme));
  // Destructive affordance stays quiet at rest and only turns red on hover,
  // so a column of delete buttons doesn't read as permanent error noise.
  const color =
    tone === 'danger'
      ? palette.textTertiary
      : palette.textSecondary;

  return {
    width: scopeEditorDensity.actionSize,
    height: scopeEditorDensity.actionSize,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    p: 0,
    color,
    '&:hover': {
      backgroundColor:
        tone === 'danger'
          ? alpha(theme.palette.error.main, 0.08)
          : palette.backgroundHover,
      color: tone === 'danger' ? theme.palette.error.main : palette.textPrimary,
    },
  };
};

// ============================================================================
// COLOR HELPERS
// ============================================================================

const getSummaryToneColor = (theme: Theme, tone: SummaryTone) => {
  const palette = getScopeEditorPalette(theme, getWorkflowWorkspacePalette(theme));

  switch (tone) {
    case 'warning':
      return palette.statusWarningText;
    case 'success':
      return palette.statusSuccessText;
    case 'info':
      return palette.statusInfoText;
    default:
      return palette.textSecondary;
  }
};

// ============================================================================
// COMPONENT PRIMITIVES
// ============================================================================

export const ScopeEditorToolbar: React.FC<{
  primary?: React.ReactNode;
  secondary?: React.ReactNode;
}> = ({ primary, secondary }) => {
  const theme = useTheme();
  const palette = useScopeEditorPalette();

  return (
    <Paper
      elevation={0}
      sx={{
        px: theme.spacing(1),
        py: theme.spacing(0.5),
        borderRadius: `${borderRadius.none}px`,
        backgroundColor: palette.backgroundCard,
        borderBottom: `1px solid ${palette.borderLight}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: layoutGap.micro,
        minHeight: scopeEditorDensity.toolbarMinHeight,
        flexShrink: 0,
      }}
    >
      <Stack direction="row" spacing={layoutGap.micro} alignItems="center" flexWrap="wrap" useFlexGap>
        {primary}
      </Stack>
      <Stack
        direction="row"
        spacing={layoutGap.micro}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ ml: { xs: 0, md: 'auto' } }}
      >
        {secondary}
      </Stack>
    </Paper>
  );
};

export const CompactStatusSummary: React.FC<{
  children: React.ReactNode;
  tone?: SummaryTone;
}> = ({ children, tone = 'default' }) => {
  const theme = useTheme();

  return (
    <Typography
      variant="caption"
      sx={{
        color: getSummaryToneColor(theme, tone),
        fontWeight: tone === 'default' ? 400 : 500,
        fontSize: scopeEditorDensity.metaFontSize,
        whiteSpace: 'nowrap',
        letterSpacing: '0.01em',
      }}
    >
      {children}
    </Typography>
  );
};

export const CompactAddAction: React.FC<ButtonProps> = ({
  children,
  startIcon,
  sx,
  ...buttonProps
}) => {
  const theme = useTheme();
  const palette = useScopeEditorPalette();

  return (
    <Button
      variant="outlined"
      size="small"
      startIcon={startIcon ?? <AddIcon sx={{ fontSize: '0.75rem' }} />}
      sx={[
        {
          borderRadius: `${borderRadius.md}px`,
          px: theme.spacing(1),
          py: 0.25,
          fontSize: scopeEditorDensity.cellFontSize,
          minHeight: scopeEditorDensity.inputHeight,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderColor: getControlBorderColor(theme),
          color: palette.textPrimary,
          backgroundColor: palette.backgroundCard,
          '& .MuiButton-startIcon': {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            ml: 0,
            mr: layoutGap.micro,
          },
          '&:hover': {
            borderColor: getControlBorderColor(theme),
            backgroundColor: palette.backgroundHover,
          },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
      {...buttonProps}
    >
      {children}
    </Button>
  );
};

export const InlineHelpIcon: React.FC<{
  label: string;
  help?: HelpValue;
  placement?: 'bottom-end' | 'bottom-start' | 'top' | 'top-start' | 'right';
}> = ({ label, help, placement = 'right' }) => {
  const theme = useTheme();
  const palette = useScopeEditorPalette();

  return (
    <HelpTooltipTrigger
      label={label}
      help={help}
      placement={placement}
      sx={{
        width: scopeEditorDensity.helpSize,
        height: scopeEditorDensity.helpSize,
        minWidth: scopeEditorDensity.helpSize,
        minHeight: scopeEditorDensity.helpSize,
        color: palette.textTertiary,
        backgroundColor: 'transparent',
        border: 'none',
        ml: 0.125,
        '&:hover, &:focus-visible': {
          color: theme.palette.primary.light,
          backgroundColor: palette.backgroundHover,
        },
      }}
    />
  );
};

export interface MappingEditorHeaderColumn {
  key: string;
  width?: number | string;
  minWidth?: number | string;
  align?: 'left' | 'center' | 'right';
  content: React.ReactNode;
}

export const MappingEditorHeader: React.FC<{
  columns: MappingEditorHeaderColumn[];
  rowRef?: React.Ref<HTMLTableRowElement>;
}> = ({ columns, rowRef }) => {
  const theme = useTheme();

  return (
    <TableHead>
      <TableRow ref={rowRef}>
        {columns.map((column) => (
          <TableCell
            key={column.key}
            sx={getScopeEditorHeaderCellSx(theme, {
              width: column.width,
              minWidth: column.minWidth,
              align: column.align,
            })}
          >
            {column.content}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

export const ScopeEditorTableVariant: React.FC<{
  ariaLabel: string;
  minWidth: number | string;
  rowHeight?: number;
  children: React.ReactNode;
}> = ({ ariaLabel, minWidth, rowHeight = scopeEditorDensity.rowHeight, children }) => {
  const theme = useTheme();
  const palette = useScopeEditorPalette();

  return (
    <TableContainer
      sx={{
        flex: 1,
        overflow: 'auto',
        backgroundColor: theme.palette.background.paper,
        minHeight: 0,
        '&::-webkit-scrollbar': { width: 6, height: 6 },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: palette.borderStrong,
          borderRadius: `${borderRadius.sm}px`,
        },
      }}
    >
      <Table
        size="small"
        stickyHeader
        aria-label={ariaLabel}
        sx={{
          tableLayout: 'fixed',
          width: '100%',
          minWidth,
          '& .MuiTableRow-root': { height: rowHeight },
          '& .MuiTableCell-root': {
            py: 0.125,
            px: layoutGap.micro,
            fontSize: scopeEditorDensity.cellFontSize,
            lineHeight: 1.2,
          },
          '& .MuiTableCell-head': {
            py: theme.spacing(0.5),
          },
          '& .MuiTableRow-hover:hover': {
            backgroundColor: palette.backgroundHover,
          },
        }}
      >
        {children}
      </Table>
    </TableContainer>
  );
};

export const MappingRow: React.FC<
  Omit<TableRowProps, 'sx'> & {
    accentColor?: string;
    interactive?: boolean;
    sx?: SxProps<Theme>;
  }
> = ({ accentColor, interactive = false, sx, hover = true, children, ...props }) => {
  const theme = useTheme();
  const palette = useScopeEditorPalette();

  return (
    <TableRow
      hover={hover}
      sx={[
        {
          '&.Mui-selected': {
            backgroundColor: palette.backgroundSelected,
          },
          ...(accentColor
            ? {
                backgroundColor: alpha(accentColor, 0.08),
                boxShadow: `inset 2px 0 0 ${accentColor}`,
              }
            : {}),
          '&:hover': {
            backgroundColor: palette.backgroundHover,
          },
          cursor: interactive ? 'pointer' : 'default',
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
      {...props}
    >
      {children}
    </TableRow>
  );
};
